import { Injectable } from "@angular/core";
import { ServiceNames } from "@models/service_enums";
import { ApicallService } from "@core-services/apicall.service";
import { map } from "rxjs/operators";
import { parseList } from "@helpers/functions";
import { CryptoWallet } from "@models";
import { MetalWallet } from "@models/asset/metal/metal_wallet";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { StockWallet } from "@models/derivative/wallets/stock_wallet";
import { EtfWallet } from "@models/derivative/wallets/etf_wallet";
import { EtcWallet } from "@models/derivative/wallets/etc_wallet";
import { LeveragedTokenWallet } from "@models/asset/cryptocoins/wallets/leveraged_token_wallet";
import { InitService } from "@core-services/init.service";
import { IndexWalletService } from "@main-services/wallet/index_wallet.service";
import { StockWalletService } from "@main-services/wallet/stock_wallet.service";
import { EtfWalletService } from "@main-services/wallet/etf_wallet.service";
import { EtcWalletService } from "@main-services/wallet/etc_wallet.service";
import { LeveragedTokenWalletService } from "@main-services/wallet/leveraged_token_wallet.service";
import { MetalWalletService } from "@main-services/wallet/metal_wallet.service";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { SecurityTokenWallet } from "@models/asset/cryptocoins/wallets/security_token_wallet";
import { SecurityTokenWalletService } from "@main-services/wallet/security_token_wallet.service";
import { EquityStockWallet } from "@models/derivative/wallets/equity_stock_wallet";
import { EquityETFWallet } from "@models/derivative/wallets/equity_etf_wallet";
import { EquityETCWallet } from "@models/derivative/wallets/equity_etc_wallet";

enum parsingNames {
	stock = "stock",
	etf = "etf",
	etc = "etc",
	leveraged = "leveraged_token",
	metal = "metal",
	index = "index",
	cryptocoin = "cryptocoin",
	commodity = "commodity",
	security = "security",
	security_token = "security_token",
	equity_security = "equity_security",
	equity_stock = "equity_stock",
	equity_etf = "equity_etf",
	equity_etc = "equity_etc",
	equity_right = "equity_right",
	equity_complex_etf = "equity_complex_etf",
	equity_complex_etc = "equity_complex_etc"
}

@Injectable({
	providedIn: "root"
})
export class AssetWalletFetchService {
	public name: string = ServiceNames.ASSET_WALLET;

	public constructor(
		private apicallService: ApicallService,
		private initService: InitService,
		private indexWalletService: IndexWalletService,
		private stockWalletService: StockWalletService,
		private etfWalletService: EtfWalletService,
		private etcWalletService: EtcWalletService,
		private leveragedTokenWalletService: LeveragedTokenWalletService,
		private metalWalletService: MetalWalletService,
		private cryptoWalletService: CryptoWalletService,
		private securityTokenWalletService: SecurityTokenWalletService
	) {}

	public initAssetWallets(): void {
		if (!this.initService.didInit([this.name])) {
			this.fetchAssetWallets();
		}
	}

	private fetchAssetWallets(): void {
		this.apicallService
			.get("asset-wallets")
			.pipe(map((x) => x["attributes"]))
			.subscribe((wallets) => {
				if (wallets[parsingNames.cryptocoin]) {
					this.cryptoWalletService.list = parseList(
						this.getWallets(wallets[parsingNames.cryptocoin]),
						CryptoWallet
					);
				}

				if (
					wallets[parsingNames.commodity] &&
					this.getWallets(wallets[parsingNames.commodity][parsingNames.metal])
				) {
					this.metalWalletService.list = parseList(
						this.getWallets(wallets[parsingNames.commodity][parsingNames.metal]),
						MetalWallet
					);
				}

				if (wallets[parsingNames.index] && this.getWallets(wallets[parsingNames.index][parsingNames.index])) {
					this.indexWalletService.list = parseList(
						this.getWallets(wallets[parsingNames.index][parsingNames.index]),
						IndexWallet
					);
				}

				if (wallets[parsingNames.leveraged]) {
					this.leveragedTokenWalletService.list = parseList(
						this.getWallets(wallets[parsingNames.leveraged]),
						LeveragedTokenWallet
					);
				}

				if (wallets[parsingNames.security_token]) {
					this.securityTokenWalletService.list = parseList(
						this.getWallets(wallets[parsingNames.security_token]),
						SecurityTokenWallet
					);
				}

				let stockWalletList: StockWallet[] = [];
				let etfWalletList: EtfWallet[] = [];
				let etcWalletList: EtcWallet[] = [];
				if (wallets[parsingNames.security]) {
					const securityWallets = wallets[parsingNames.security];
					if (securityWallets[parsingNames.stock]) {
						stockWalletList = parseList(this.getWallets(securityWallets[parsingNames.stock]), StockWallet);
					}
					if (securityWallets[parsingNames.etf]) {
						etfWalletList = parseList(this.getWallets(securityWallets[parsingNames.etf]), EtfWallet);
					}
					if (securityWallets[parsingNames.etc]) {
						etcWalletList = parseList(this.getWallets(securityWallets[parsingNames.etc]), EtcWallet);
					}
				}

				if (wallets[parsingNames.equity_security]) {
					const equitySecurityWallets = wallets[parsingNames.equity_security];
					if (equitySecurityWallets[parsingNames.equity_stock]) {
						stockWalletList = stockWalletList.concat(
							parseList(
								this.getWallets(equitySecurityWallets[parsingNames.equity_stock]),
								EquityStockWallet
							)
						);
					}

					if (equitySecurityWallets[parsingNames.equity_etf]) {
						etfWalletList = etfWalletList.concat(
							parseList(this.getWallets(equitySecurityWallets[parsingNames.equity_etf]), EquityETFWallet)
						);
					}

					if (equitySecurityWallets[parsingNames.equity_etc]) {
						etcWalletList = etcWalletList.concat(
							parseList(this.getWallets(equitySecurityWallets[parsingNames.equity_etc]), EquityETCWallet)
						);
					}
				}

				this.stockWalletService.list = stockWalletList;
				this.etfWalletService.list = etfWalletList;
				this.etcWalletService.list = etcWalletList;
			});
	}

	private getWallets(walletToParse: object): object[] {
		return walletToParse["attributes"]["wallets"];
	}
}
